/**
 * Service facade for the datastore feature.
 *
 * Routes every call to the in-memory mock (datastore.mock.ts) when the
 * VUE_APP_DATASTORE_MOCK parameter is "true", and otherwise to the real
 * datastore-service REST surface through the gateway proxy
 * (/rest/v1/datastore/**, see docs/architecture/datastore-datastore-design.md §11.1).
 *
 * Both feature flags are configuration parameters read via getParameter —
 * never hardcoded (see the oneteg-dev convention).
 *
 * Two translations happen here and nowhere else, because the pages are written
 * against a simpler contract than the API deliberately offers:
 *
 *  - Paging. The facade is offset-shaped (page/pageSize) while the API is
 *    cursor-first with a capped OFFSET (design §7.2). Cursors returned by the
 *    API are remembered per page so sequential navigation uses them, and a jump
 *    past the offset cap walks cursors forward instead of failing.
 *  - Sharing. The dialog stages changes and saves them in one action, while the
 *    API keeps grants and revocations on separate paths so the gateway can tell
 *    them apart. applyShareChanges is where one becomes the other.
 */
import { ref } from "vue";
import {
  apiCall,
  apiCallCustom,
  apiCallParams,
  getTenantId,
  Response,
} from "@/core/api";
import { getParameter } from "@/core/util";
import { hasPermission, isOrganizationAdmin } from "@/core/auth";
import { getConfiguration } from "@/configuration/services/configuration.service";
import {
  ColumnMapping,
  ConvertPreview,
  DataStoreCell,
  DataStoreField,
  DataStoreFieldType,
  DataStoreJob,
  DataStoreLimits,
  DataStoreAccessLevel,
  DataStoreRow,
  DataStoreTable,
  ImportErrorMode,
  ImportMode,
  ImportPreview,
  RowPage,
  RowQuery,
  ShareTargetPage,
} from "@/datastore/types";
import {
  mockAddField,
  mockCancelJob,
  mockConvertPreview,
  mockCreateTable,
  mockDeleteField,
  mockDeleteRow,
  mockDeleteRows,
  mockDeleteTable,
  mockDownloadExport,
  mockGetJob,
  mockGetRows,
  mockGetTable,
  mockListTables,
  mockPrivileges,
  mockRunImportJob,
  mockApplyShares,
  mockSaveRow,
  mockShareTargets,
  mockStartExport,
  mockStartImport,
  mockUpdateField,
  mockUpdateTable,
} from "@/datastore/services/datastore.mock";
import { chunkIds } from "@/datastore/utils/batchChunks";
import { isConfigTrue } from "@/datastore/utils/configFlag";

let enabledCache: boolean | null = null;
let mockCache: boolean | null = null;

export async function isDataStoreEnabled(): Promise<boolean> {
  if (enabledCache === null) {
    enabledCache = (await getParameter("DATASTORE_ENABLED")) === "true";
  }
  return enabledCache;
}

export async function isDataStoreMockMode(): Promise<boolean> {
  if (mockCache === null) {
    mockCache = (await getParameter("DATASTORE_MOCK")) === "true";
  }
  return mockCache;
}

/** Synchronous variant for templates; resolved during module bootstrap. */
export const dataStoreMockMode = ref(false);
isDataStoreMockMode().then((mock) => {
  dataStoreMockMode.value = mock;
});

/** The key the gateway and datastore-service both enforce; kept here so the UI states it once. */
export const DATASTORE_SHARE_TABLE_KEY = "datastore.share.table";

/**
 * Whether this tenant may share datastore tables across organizations — the backend-owned
 * `datastore.share.table` parameter, read through the same config endpoint the backend
 * enforces it from, so the UI never disagrees with the service about what is allowed.
 *
 * Deliberately NOT memoized, unlike DATASTORE_ENABLED / DATASTORE_MOCK above. Those two come
 * from the unauthenticated config.json and can safely be resolved once at module
 * bootstrap; this is an authenticated call, so a bootstrap read would happen before
 * login, get a 401, and latch "off" for the whole session. Caching it later instead
 * only moves the problem: an operator flipping the parameter would leave every open
 * tab offering a share the backend now refuses. It is read when the share UI is
 * about to be drawn, which is a human-frequency action, and the row is second-level
 * cached on the server.
 */
export async function isDataStoreSharingEnabled(): Promise<boolean> {
  // Mock mode has no backend to ask, and the share widget is part of what it demonstrates.
  if (await isDataStoreMockMode()) return true;
  try {
    const config = await getConfiguration(
      DATASTORE_SHARE_TABLE_KEY,
      "TENANT",
      getTenantId()
    );
    return isConfigTrue(config?.value);
  } catch {
    // Unreachable or unauthorized means we do not know, and the honest render for
    // "do not know" is the same as "off" — it hides an action the backend would refuse.
    return false;
  }
}

/**
 * Privilege check for datastore actions. In mock mode the check is served by the
 * mock privilege store so gating can be demonstrated without seeded
 * privileges; otherwise it is the platform hasPermission over the DATA_STORE.*
 * privileges. The backend enforces the same privileges at the gateway proxy —
 * this call only decides what the user is shown.
 */
export function canDataStore(privilege: string): boolean {
  const key = `DATA_STORE.${privilege}`;
  if (dataStoreMockMode.value) return mockPrivileges[key] !== false;
  return hasPermission(key);
}

/**
 * Whether this user may delete this table. Both halves of the rule have to hold and neither implies
 * the other: the organization the user is acting for has to own the table, which datastore-service
 * enforces with requireOwner, and the user has to administer that organization, which the gateway
 * enforces in front of the delete. An Edit Table grant reshapes a table but never takes it away from
 * its owner, and an administrator of another organization gets no further than ownership.
 *
 * The prototype has no organization role to read, so it answers from the mock privileges the way
 * {@link canDataStore} does rather than hiding the action outright.
 */
export function canDeleteTable(table: DataStoreTable): boolean {
  if (!canDataStore("TABLE.DELETE") || !table.owned) return false;
  return dataStoreMockMode.value || isOrganizationAdmin();
}

const BASE = "/datastore";

/* ------------------------------------------------------------------ plumbing */

/**
 * Unwraps a gateway response. datastore refusals carry a machine-readable `code`
 * and a human `message`; the message is what the pages surface, so it is
 * preserved rather than replaced with a generic string.
 */
function unwrap<T>(response: Response, fallback: string): T {
  if (response.code >= 200 && response.code < 300) {
    return response.result as T;
  }
  const body = response.result as
    | { code?: string; message?: string }
    | undefined;
  const error: Error & { datastoreCode?: string } = new Error(
    body?.message ?? response.message ?? fallback
  );
  error.datastoreCode = body?.code;
  throw error;
}

/* ---------------------------------------------------------------- wire types */

interface WireField {
  id: string;
  name: string;
  type: DataStoreFieldType;
  ordinal: number;
  isPrimary: boolean;
  required: boolean;
  uniqueKey: boolean;
  dbIndex: boolean;
  settings?: Record<string, unknown>;
}

interface WireShare {
  organizationId: string;
  organizationName: string;
  access: string;
}

interface WireTable {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  organizationName?: string;
  rowCount: number;
  version: number;
  owned: boolean;
  access: string;
  sharedWith: WireShare[];
  modifiedAt: string;
  fields: WireField[];
}

interface WireRowPage {
  rows: DataStoreRow[];
  total: number;
  totalExact: boolean;
  offset: number;
  nextCursor: string | null;
}

interface WireJob {
  id: string;
  type: "IMPORT" | "EXPORT" | "CONVERT" | "COPY" | "DROP";
  tableId: string;
  state: DataStoreJob["state"];
  progressPercentage: number;
  processedRows: number;
  totalRows: number;
  report?: string[];
  error?: string;
}

function toField(field: WireField): DataStoreField {
  return {
    id: field.id,
    name: field.name,
    type: field.type,
    ordinal: field.ordinal,
    isPrimary: field.isPrimary,
    required: field.required,
    uniqueKey: field.uniqueKey,
    dbIndex: field.dbIndex,
    settings: (field.settings ?? {}) as DataStoreField["settings"],
  };
}

function toTable(table: WireTable): DataStoreTable {
  const shares = table.sharedWith ?? [];
  return {
    id: table.id,
    name: table.name,
    description: table.description ?? "",
    organizationId: table.organizationId,
    organizationName: table.organizationName ?? "",
    rowCount: table.rowCount,
    version: table.version,
    // The owner's level is not carried on the wire as MODIFY, so it is derived here rather than
    // left for every caller to remember that `owned` outranks `access`.
    owned: table.owned ?? false,
    access: table.owned ? "MODIFY" : toAccessLevel(table.access),
    sharedWith: shares.map((share) => ({
      organizationId: share.organizationId,
      name: share.organizationName,
      access: toAccessLevel(share.access),
    })),
    modifiedAt: table.modifiedAt ?? "",
    fields: (table.fields ?? []).map(toField),
  };
}

/** An unrecognized level reads as the most restrictive one rather than as full access. */
function toAccessLevel(access: string | undefined): DataStoreAccessLevel {
  return access === "MODIFY" || access === "READ_WRITE" ? access : "READ";
}

function toJob(job: WireJob): DataStoreJob {
  return {
    id: job.id,
    type: (job.type === "COPY" || job.type === "DROP"
      ? "IMPORT"
      : job.type) as DataStoreJob["type"],
    tableId: job.tableId,
    state: job.state,
    progressPercentage: job.progressPercentage ?? 0,
    processedRows: job.processedRows ?? 0,
    totalRows: job.totalRows ?? 0,
    report: job.report ?? [],
    error: job.error,
  };
}

/* ------------------------------------------------------------------- tables */

/**
 * The documented §10 defaults, used only when the limits endpoint cannot be
 * reached. They are a last resort, never the source of truth: an operator who
 * retunes a tenant must see the retuned number, which is why the real values
 * come from the service that enforces them.
 */
export const DEFAULT_DATASTORE_LIMITS: DataStoreLimits = {
  maxTablesPerOrg: 50,
  maxFieldsPerTable: 100,
  maxRowsPerTable: 5_000_000,
  maxRowsPerOrg: 10_000_000,
  maxCellBytes: 4_000,
  pageSizeLimit: 200,
  batchRowsLimit: 200,
  maxOffset: 100_000,
  importMaxFileBytes: 2_147_483_648,
};

let limitsCache: DataStoreLimits | null = null;

/** Effective governor limits for the caller's organization; cached per session. */
export async function getLimits(): Promise<DataStoreLimits> {
  if (limitsCache) return limitsCache;
  if (await isDataStoreMockMode()) {
    limitsCache = DEFAULT_DATASTORE_LIMITS;
    return limitsCache;
  }
  try {
    const response = await apiCall("GET", `${BASE}/limits`);
    const wire = unwrap<Partial<DataStoreLimits>>(
      response,
      "Could not read the limits"
    );
    limitsCache = { ...DEFAULT_DATASTORE_LIMITS, ...positiveOnly(wire) };
  } catch {
    // A limits lookup must never keep the page from rendering.
    limitsCache = DEFAULT_DATASTORE_LIMITS;
  }
  return limitsCache;
}

/** Guards against a misconfigured zero or a non-numeric value reaching the gauge. */
function positiveOnly(
  wire: Partial<DataStoreLimits>
): Partial<DataStoreLimits> {
  const clean: Record<string, number> = {};
  Object.entries(wire ?? {}).forEach(([key, value]) => {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) clean[key] = parsed;
  });
  return clean as Partial<DataStoreLimits>;
}

export async function listTables(search?: string): Promise<DataStoreTable[]> {
  if (await isDataStoreMockMode()) return mockListTables(search);
  const response = await apiCallParams(
    "GET",
    `${BASE}/tables`,
    search ? { search } : undefined
  );
  return unwrap<WireTable[]>(response, "Could not load tables").map(toTable);
}

export async function getTable(id: string): Promise<DataStoreTable> {
  if (await isDataStoreMockMode()) return mockGetTable(id);
  const response = await apiCall("GET", `${BASE}/tables/${id}`);
  return toTable(unwrap<WireTable>(response, "Could not load the table"));
}

export async function createTable(
  name: string,
  description: string
): Promise<DataStoreTable> {
  if (await isDataStoreMockMode()) return mockCreateTable(name, description);
  const response = await apiCall("POST", `${BASE}/tables`, {
    name,
    description,
  });
  return toTable(unwrap<WireTable>(response, "Could not create the table"));
}

export async function updateTable(
  id: string,
  patch: { name?: string; description?: string }
): Promise<void> {
  if (await isDataStoreMockMode()) {
    await mockUpdateTable(id, patch);
    return;
  }
  const response = await apiCall("PUT", `${BASE}/tables/${id}`, patch);
  unwrap<WireTable>(response, "Could not save the table");
  // A rename may change what a cursor page means; drop the cached positions.
  clearCursorCache(id);
}

export async function deleteTable(id: string): Promise<void> {
  if (await isDataStoreMockMode()) {
    await mockDeleteTable(id);
    return;
  }
  const response = await apiCall("DELETE", `${BASE}/tables/${id}`);
  unwrap<void>(response, "Could not delete the table");
  clearCursorCache(id);
}

/* ------------------------------------------------------------------ sharing */

/**
 * One page of organizations this table can be shared with, searched and paged in the database.
 * Organizations that already hold a grant are included rather than filtered out — the dialog marks
 * them, which keeps a page of fifty fifty rows long.
 */
export async function searchShareTargets(
  search: string,
  page: number,
  size: number
): Promise<ShareTargetPage> {
  if (await isDataStoreMockMode()) return mockShareTargets(search, page, size);
  // With sharing off the service answers with an empty page by design; skipping the call
  // saves a round trip whose result is already known.
  if (!(await isDataStoreSharingEnabled())) {
    return { targets: [], total: 0, page, size };
  }
  const response = await apiCallParams("GET", `${BASE}/tables/share-targets`, {
    search: search ?? "",
    page,
    size,
  });
  return unwrap<ShareTargetPage>(
    response,
    "Could not load the organizations this table can be shared with"
  );
}

/**
 * Applies a staged set of grants as one transaction — the sharing dialog's Save.
 *
 * Grants and revocations go to separate endpoints on purpose, and this is the one place that
 * matters on the client: the gateway decides from the path alone whether the datastore.share.table
 * parameter applies, and it must never apply to a revoke. Revocations are sent first so that a
 * refused grant cannot leave an organization holding access the user has already taken away.
 */
export async function applyShareChanges(
  id: string,
  grants: { organizationId: string; access: DataStoreAccessLevel }[],
  revoked: string[]
): Promise<void> {
  if (await isDataStoreMockMode()) {
    await mockApplyShares(id, grants, revoked);
    return;
  }
  if (revoked.length > 0) {
    const response = await apiCall(
      "POST",
      `${BASE}/tables/${id}/shares/batch-revoke`,
      { organizationIds: revoked }
    );
    unwrap<WireShare[]>(response, "Could not revoke the shares");
  }
  if (grants.length > 0) {
    const response = await apiCall(
      "POST",
      `${BASE}/tables/${id}/shares/batch`,
      {
        grants,
      }
    );
    unwrap<WireShare[]>(response, "Could not share the table");
  }
}

/* ------------------------------------------------------------------- fields */

export async function addField(
  tableId: string,
  def: Omit<DataStoreField, "id" | "ordinal">
): Promise<DataStoreField> {
  if (await isDataStoreMockMode()) return mockAddField(tableId, def);
  const response = await apiCall("POST", `${BASE}/tables/${tableId}/fields`, {
    name: def.name,
    type: def.type,
    required: def.required ?? false,
    uniqueKey: def.uniqueKey ?? false,
    dbIndex: def.dbIndex ?? false,
    settings: def.settings ?? {},
  });
  return toField(unwrap<WireField>(response, "Could not add the field"));
}

export async function updateField(
  tableId: string,
  fieldId: string,
  patch: Partial<DataStoreField>
): Promise<void> {
  if (await isDataStoreMockMode()) {
    await mockUpdateField(tableId, fieldId, patch);
    return;
  }
  const response = await apiCall("PUT", `${BASE}/fields/${fieldId}`, {
    name: patch.name,
    type: patch.type,
    required: patch.required,
    uniqueKey: patch.uniqueKey,
    dbIndex: patch.dbIndex,
    settings: patch.settings,
  });
  // A type change answers with the id of the CONVERT job that does the work;
  // the drawer already tells the user the conversion was queued.
  unwrap<{ field: WireField; convertJobId?: string }>(
    response,
    "Could not save the field"
  );
  clearCursorCache(tableId);
}

export async function deleteField(
  tableId: string,
  fieldId: string
): Promise<void> {
  if (await isDataStoreMockMode()) {
    await mockDeleteField(tableId, fieldId);
    return;
  }
  const response = await apiCall("DELETE", `${BASE}/fields/${fieldId}`);
  unwrap<void>(response, "Could not delete the field");
  clearCursorCache(tableId);
}

export async function convertPreview(
  tableId: string,
  fieldId: string,
  toType: DataStoreFieldType
): Promise<ConvertPreview> {
  if (await isDataStoreMockMode()) {
    return mockConvertPreview(tableId, fieldId, toType);
  }
  const response = await apiCall(
    "POST",
    `${BASE}/fields/${fieldId}/convert/preview`,
    { toType }
  );
  return unwrap<ConvertPreview>(response, "Could not run the dry run");
}

/* --------------------------------------------------------------------- rows */

/**
 * Cursors already learned, keyed by table + page size + search term. Page 1
 * needs no cursor; page N+1's cursor is whatever the response for page N
 * returned. This is what makes ordinary next/previous navigation O(1) at any
 * depth instead of a growing OFFSET.
 */
const cursorCache = new Map<string, Map<number, string>>();

/** How many cursor hops a jump past the offset cap may cost before giving up. */
const MAX_CURSOR_WALK = 40;

function cursorKey(tableId: string, query: RowQuery): string {
  return `${tableId}|${query.pageSize}|${query.search ?? ""}`;
}

function clearCursorCache(tableId: string): void {
  [...cursorCache.keys()]
    .filter((key) => key.startsWith(`${tableId}|`))
    .forEach((key) => cursorCache.delete(key));
}

function rememberCursor(
  key: string,
  page: number,
  cursor: string | null
): void {
  if (!cursor) return;
  const pages = cursorCache.get(key) ?? new Map<number, string>();
  pages.set(page + 1, cursor);
  cursorCache.set(key, pages);
}

async function fetchRowPage(
  tableId: string,
  params: Record<string, string | number>
): Promise<WireRowPage> {
  const response = await apiCallParams(
    "GET",
    `${BASE}/tables/${tableId}/rows`,
    params
  );
  return unwrap<WireRowPage>(response, "Could not load rows");
}

export async function getRows(
  tableId: string,
  query: RowQuery
): Promise<RowPage> {
  if (await isDataStoreMockMode()) return mockGetRows(tableId, query);

  const key = cursorKey(tableId, query);
  const pages = cursorCache.get(key);
  const size = query.pageSize;
  const offset = (query.page - 1) * size;
  const search = query.search ? { search: query.search } : {};

  let wire: WireRowPage;
  const known = pages?.get(query.page);
  if (query.page === 1) {
    wire = await fetchRowPage(tableId, { size, ...search });
  } else if (known) {
    wire = await fetchRowPage(tableId, { size, after: known, ...search });
  } else {
    try {
      wire = await fetchRowPage(tableId, { size, page: query.page, ...search });
    } catch (error) {
      const code = (error as { datastoreCode?: string }).datastoreCode;
      if (code !== "OFFSET_CAP_EXCEEDED") throw error;
      // Past the cap the API only answers to cursors, so walk forward from the
      // deepest position already known rather than surfacing a dead end.
      wire = await walkToPage(tableId, key, query, size, search);
    }
  }

  rememberCursor(key, query.page, wire.nextCursor);
  return {
    rows: wire.rows,
    total: wire.total,
    // The API reports offset 0 for a cursor read; the grid numbers rows from
    // the page it asked for, so the facade supplies that.
    offset,
    nextCursor: wire.nextCursor,
  };
}

async function walkToPage(
  tableId: string,
  key: string,
  query: RowQuery,
  size: number,
  search: Record<string, string>
): Promise<WireRowPage> {
  const pages = cursorCache.get(key) ?? new Map<number, string>();
  let from = 1;
  let cursor: string | undefined;
  [...pages.keys()]
    .filter((page) => page <= query.page)
    .sort((a, b) => a - b)
    .forEach((page) => {
      from = page;
      cursor = pages.get(page);
    });
  if (query.page - from > MAX_CURSOR_WALK) {
    throw new Error(
      `Page ${query.page} is too far past the offset cap to reach directly. ` +
        "Narrow the search or page forward instead."
    );
  }
  let wire = await fetchRowPage(
    tableId,
    cursor ? { size, after: cursor, ...search } : { size, ...search }
  );
  rememberCursor(key, from, wire.nextCursor);
  for (let page = from; page < query.page; page++) {
    if (!wire.nextCursor) break;
    wire = await fetchRowPage(tableId, {
      size,
      after: wire.nextCursor,
      ...search,
    });
    rememberCursor(key, page + 1, wire.nextCursor);
  }
  return wire;
}

export async function saveRow(
  tableId: string,
  rowId: string | null,
  cells: Record<string, DataStoreCell>
): Promise<DataStoreRow> {
  if (await isDataStoreMockMode()) return mockSaveRow(tableId, rowId, cells);
  const response = rowId
    ? await apiCall("PATCH", `${BASE}/tables/${tableId}/rows/${rowId}`, {
        cells,
      })
    : await apiCall("POST", `${BASE}/tables/${tableId}/rows`, { cells });
  const saved = unwrap<DataStoreRow>(response, "Could not save the row");
  if (!rowId) clearCursorCache(tableId);
  return saved;
}

export async function deleteRow(tableId: string, rowId: string): Promise<void> {
  if (await isDataStoreMockMode()) {
    await mockDeleteRow(tableId, rowId);
    return;
  }
  const response = await apiCall(
    "DELETE",
    `${BASE}/tables/${tableId}/rows/${rowId}`
  );
  unwrap<void>(response, "Could not delete the row");
  clearCursorCache(tableId);
}

/**
 * Deletes a set of rows. The API refuses a batch larger than batchRowsLimit
 * (design §10) rather than trimming it, so the selection is split into batches
 * the service will accept instead of surfacing a limit error the user cannot act
 * on. The count comes back from the service — the caller reports what was
 * actually deleted, not what it asked for, because ids that are already gone
 * delete zero rows.
 *
 * @returns how many rows the service reports deleted
 */
export async function deleteRows(
  tableId: string,
  rowIds: string[]
): Promise<number> {
  if (rowIds.length === 0) return 0;
  if (await isDataStoreMockMode()) return mockDeleteRows(tableId, rowIds);
  const limits = await getLimits();
  const batches = chunkIds(rowIds, limits.batchRowsLimit);
  let deleted = 0;
  try {
    // Sequential on purpose: the service takes a table-level write lock per
    // batch, so firing them together would only queue behind itself.
    for (let i = 0; i < batches.length; i++) {
      const response = await apiCall(
        "POST",
        `${BASE}/tables/${tableId}/rows/batch-delete`,
        { rowIds: batches[i] }
      );
      const result = unwrap<{ deleted?: number }>(
        response,
        "Could not delete the rows"
      );
      deleted += result?.deleted ?? 0;
    }
  } finally {
    // Even a failed batch may have deleted the ones before it, so the cached
    // cursor positions are stale either way.
    clearCursorCache(tableId);
  }
  return deleted;
}

/* --------------------------------------------------------------------- jobs */

export async function startImport(
  tableId: string,
  file: File,
  header: string[],
  sampleRows: string[][]
): Promise<ImportPreview> {
  if (await isDataStoreMockMode()) {
    return mockStartImport(tableId, file, header, sampleRows);
  }
  // Only the file is sent. The browser's own header/sample are the mapping
  // step's convenience; the preview that comes back is the server's reading of
  // the staged bytes, which is the one the import will actually use (§7.1).
  const form = new FormData();
  form.append("file", file, file.name);
  const response = await apiCallCustom(
    "POST",
    `${BASE}/tables/${tableId}/import`,
    { body: form, timeoutMs: 30 * 60 * 1000 }
  );
  return unwrap<ImportPreview>(response, "Could not upload the file");
}

export async function runImportJob(
  jobId: string,
  mapping: ColumnMapping[],
  sampleRows: string[][],
  mode: ImportMode = "append",
  errorMode: ImportErrorMode = "partial"
): Promise<void> {
  if (await isDataStoreMockMode()) {
    await mockRunImportJob(jobId, mapping, sampleRows);
    return;
  }
  const response = await apiCall("POST", `${BASE}/jobs/${jobId}/start`, {
    mapping,
    mode,
    errorMode,
  });
  unwrap<WireJob>(response, "Could not start the import");
}

export async function startExport(tableId: string): Promise<DataStoreJob> {
  if (await isDataStoreMockMode()) return mockStartExport(tableId);
  const response = await apiCall(
    "POST",
    `${BASE}/tables/${tableId}/export`,
    {}
  );
  return toJob(unwrap<WireJob>(response, "Could not start the export"));
}

export async function getJob(jobId: string): Promise<DataStoreJob> {
  if (await isDataStoreMockMode()) return mockGetJob(jobId);
  const response = await apiCall("GET", `${BASE}/jobs/${jobId}`);
  return toJob(unwrap<WireJob>(response, "Could not read the job"));
}

export async function cancelJob(jobId: string): Promise<void> {
  if (await isDataStoreMockMode()) {
    await mockCancelJob(jobId);
    return;
  }
  const response = await apiCall("POST", `${BASE}/jobs/${jobId}/cancel`, {});
  unwrap<WireJob>(response, "Could not cancel the job");
}

export async function downloadExport(jobId: string): Promise<Blob> {
  if (await isDataStoreMockMode()) return mockDownloadExport(jobId);
  const response = await apiCallCustom(
    "GET",
    `${BASE}/jobs/${jobId}/download`,
    {
      responseType: "blob",
      preserveResponseBody: true,
      timeoutMs: 30 * 60 * 1000,
    }
  );
  if (response.code < 200 || response.code >= 300) {
    throw new Error(response.message ?? "Could not download the export");
  }
  const body = response.result;
  return body instanceof Blob ? body : new Blob([body], { type: "text/csv" });
}
